import { plainToClass } from "class-transformer";
import { toJS } from "mobx";
import IsdsResponse from "models/isdsResponse";
import LicensePlateChange from "models/licensePlateChange";
import EshopRequestBuilder from "../data/eshopRequestBuilder";
import EshopError from "../models/eshopError";
import AuthorizationService from "../services/authorizationService";
import RepositoryBase from "./repositoryBase";
import { parseFileNameFromHeaderResponse, persistedBlobToISDSFormat } from "../utils/fileUtils";
import { PersistedBase64 } from "../models/notice";
import EventBus from "../services/eventBus";
import VehicleChangeApplicationProductionResponse from "entities/vehicleChangeApplicationProductionResponse";
import BlobWithFileName from "models/blobWithFileName";

export const POST_LICENSE_PLATE_CHANGE_REQUEST_FAILURE = "POST_LICENSE_PLATE_CHANGE_REQUEST_FAILURE";
export const POST_LICENSE_PLATE_CHANGE_PREVIEW_REQUEST_FAILURE = "POST_LICENSE_PLATE_CHANGE_PREVIEW_REQUEST_FAILURE";
const POST_LICENSE_PLATE_CHANGE_APPLICATIONS_DOCUMENTS_ISDS_FAILURE =
  "POST_LICENSE_PLATE_CHANGE_APPLICATIONS_DOCUMENTS_ISDS_FAILURE";

export default class LicensePlateChangeRepository extends RepositoryBase<EshopRequestBuilder> {
  constructor(
    protected apiFactory: ReturnType<typeof EshopRequestBuilder.Factory>,
    protected authorizationService: AuthorizationService,
    protected eventBus: EventBus
  ) {
    super(() => apiFactory(), authorizationService, eventBus);
  }

  createRequestObj(licensePlateChange: LicensePlateChange) {
    const notice = toJS(licensePlateChange);

    notice.originalVehicle = notice?.vehicle;
    notice.newVehicle = {
      countryId: notice.countryId,
      licensePlate: notice.licensePlate,
    };
    notice.vehicleOperator = undefined;
    notice.vehicle = undefined;
    notice.countryId = undefined;
    notice.licensePlate = undefined;

    return notice;
  }

  createLicensePlateChangeNotice(licensePlateChange: LicensePlateChange): Promise<EshopError | BlobWithFileName> {
    const notice = this.createRequestObj(licensePlateChange);

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`charge_registrations/vehicle_change_applications`)
        .clearPost(notice, undefined, undefined, undefined, "application/pdf")
        .then(async (response: any) => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          const fileName = parseFileNameFromHeaderResponse(response.headers.get("Content-Disposition"));
          const blob = await response.blob();

          return { blob: blob, fileName: fileName ?? "download" } as BlobWithFileName;
        })
        .catch(() => {
          throw new EshopError(POST_LICENSE_PLATE_CHANGE_REQUEST_FAILURE);
        })
    );
  }
  getProduction(licensePlateChange: LicensePlateChange): Promise<VehicleChangeApplicationProductionResponse | EshopError> {
    const notice = this.createRequestObj(licensePlateChange);

    return this.callApi(() =>
      this.apiFactory()
        .apiVersion(3)
        .one("charge_registrations/vehicle_change_application_preview")
        .post(notice)
        .then(response => {
          return plainToClass(VehicleChangeApplicationProductionResponse, response);
        })
        .catch(() => new EshopError(POST_LICENSE_PLATE_CHANGE_PREVIEW_REQUEST_FAILURE))
    );
  }

  uploadDocuments(ISDSSessionId: string, files: PersistedBase64[]) {
    const data = new FormData();
    files.forEach(persistedBlob => data.append("documentFiles", persistedBlobToISDSFormat(persistedBlob)));

    return this.callApi(api =>
      api
        .apiVersion(3)
        .one(`charge_registrations/vehicle_change_applications/documents/isds`)
        .postData(data, ISDSSessionId)
        .then(async response => {
          if (!response.ok) return plainToClass(EshopError, (await response.json()) as EshopError);

          return plainToClass(IsdsResponse, (await response.json()) as IsdsResponse);
        })
        .catch(() => {
          throw new EshopError(POST_LICENSE_PLATE_CHANGE_APPLICATIONS_DOCUMENTS_ISDS_FAILURE);
        })
    );
  }
}
